import React, { useState } from 'react';

const SchemaTable = ({ columns, tableName }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const getTypeColor = (type) => {
    const typeStr = (type || '').toLowerCase();
    
    // Character/String types
    if (typeStr.includes('char') || typeStr.includes('text') || typeStr.includes('varchar') || typeStr.includes('nvarchar')) {
      return 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700';
    }
    
    // Numeric types
    if (typeStr.includes('int') || typeStr.includes('numeric') || typeStr.includes('decimal') || typeStr.includes('float') || typeStr.includes('money')) {
      return 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700';
    }
    
    // Date/Time types
    if (typeStr.includes('date') || typeStr.includes('time') || typeStr.includes('posix')) {
      return 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700';
    }
    
    // Boolean/Logical types
    if (typeStr.includes('bool') || typeStr.includes('logical') || typeStr.includes('bit')) {
      return 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700';
    }
    
    // Default
    return 'bg-gray-100 dark:bg-marble-700/40 text-gray-700 dark:text-marble-300 border-gray-300 dark:border-marble-600';
  };

  const getCompleteness = (column) => {
    const total = column.count || 0;
    const nulls = column.na_count || 0;
    if (total === 0) return 0;
    return ((total - nulls) / total * 100).toFixed(1);
  };

  const getUniqueness = (column) => {
    const total = column.count || 0;
    const unique = column.unique_vals || 0;
    if (total === 0) return 0;
    return ((unique / total) * 100).toFixed(1);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedColumns = () => {
    if (!sortConfig.key) return columns;

    return [...columns].sort((a, b) => {
      let aVal, bVal;

      // Handle special computed columns
      if (sortConfig.key === 'completeness') {
        aVal = parseFloat(getCompleteness(a));
        bVal = parseFloat(getCompleteness(b));
      } else if (sortConfig.key === 'uniqueness') {
        aVal = parseFloat(getUniqueness(a));
        bVal = parseFloat(getUniqueness(b));
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];

        // Handle numeric sorting
        if (sortConfig.key === 'count' || sortConfig.key === 'unique_vals' || sortConfig.key === 'na_count') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else {
          // String sorting
          aVal = String(aVal || '').toLowerCase();
          bVal = String(bVal || '').toLowerCase();
        }
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return (
        <svg className="w-4 h-4 ml-2 text-marble-600 opacity-50 group-hover:opacity-100 transition-opacity" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5 12a1 1 0 102 0V6.414l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L5 6.414V12zM15 8a1 1 0 10-2 0v5.586l-1.293-1.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L15 13.586V8z" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-2 text-primary-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
        <path d="M3 3a1 1 0 000 2h11a1 1 0 100-2H3zM3 7a1 1 0 000 2h5a1 1 0 000-2H3zM3 11a1 1 0 100 2h4a1 1 0 100-2H3zM13 16a1 1 0 102 0v-5.586l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 101.414 1.414L13 10.414V16z" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-2 text-primary-400 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
        <path d="M3 3a1 1 0 000 2h11a1 1 0 100-2H3zM13 4a1 1 0 100 2h4a1 1 0 100-2h-4zM13 8a1 1 0 100 2h4a1 1 0 100-2h-4zM3 7a1 1 0 000 2h5a1 1 0 000-2H3zM3 11a1 1 0 100 2h1a1 1 0 100-2H3zM7 16a1 1 0 102 0v-5.586l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 101.414 1.414L7 10.414V16z" />
      </svg>
    );
  };

  const sortedColumns = getSortedColumns();

  return (
    <div className="card-column">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800 dark:text-primary-200 transition-colors">
            Schema Definition
          </h2>
          <span className="text-sm text-gray-600 dark:text-marble-400 transition-colors">
            {columns.length} Columns
          </span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-primary-600/50">
                <th 
                  onClick={() => handleSort('column')}
                  className="text-left py-4 px-4 text-sm font-bold text-primary-700 dark:text-primary-300 uppercase tracking-wider cursor-pointer hover:text-primary-600 dark:hover:text-primary-200 transition-colors"
                >
                  <div className="flex items-center">
                    Column Name
                    <SortIcon columnKey="column" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('type')}
                  className="text-left py-4 px-4 text-sm font-bold text-primary-700 dark:text-primary-300 uppercase tracking-wider cursor-pointer hover:text-primary-600 dark:hover:text-primary-200 transition-colors"
                >
                  <div className="flex items-center">
                    Data Type
                    <SortIcon columnKey="type" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('count')}
                  className="text-right py-4 px-4 text-sm font-bold text-primary-700 dark:text-primary-300 uppercase tracking-wider cursor-pointer hover:text-primary-600 dark:hover:text-primary-200 transition-colors"
                >
                  <div className="flex items-center justify-end">
                    Row Count
                    <SortIcon columnKey="count" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('unique_vals')}
                  className="text-right py-4 px-4 text-sm font-bold text-primary-700 dark:text-primary-300 uppercase tracking-wider cursor-pointer hover:text-primary-600 dark:hover:text-primary-200 transition-colors"
                >
                  <div className="flex items-center justify-end">
                    Unique Values
                    <SortIcon columnKey="unique_vals" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('na_count')}
                  className="text-right py-4 px-4 text-sm font-bold text-primary-700 dark:text-primary-300 uppercase tracking-wider cursor-pointer hover:text-primary-600 dark:hover:text-primary-200 transition-colors"
                >
                  <div className="flex items-center justify-end">
                    Null Count
                    <SortIcon columnKey="na_count" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('completeness')}
                  className="text-right py-4 px-4 text-sm font-bold text-primary-700 dark:text-primary-300 uppercase tracking-wider cursor-pointer hover:text-primary-600 dark:hover:text-primary-200 transition-colors"
                >
                  <div className="flex items-center justify-end">
                    Completeness
                    <SortIcon columnKey="completeness" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('uniqueness')}
                  className="text-right py-4 px-4 text-sm font-bold text-primary-700 dark:text-primary-300 uppercase tracking-wider cursor-pointer hover:text-primary-600 dark:hover:text-primary-200 transition-colors"
                >
                  <div className="flex items-center justify-end">
                    Uniqueness
                    <SortIcon columnKey="uniqueness" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedColumns.map((column, index) => {
                const completeness = parseFloat(getCompleteness(column));
                const uniqueness = parseFloat(getUniqueness(column));
                
                return (
                  <tr 
                    key={index}
                    className="border-b border-gray-200 dark:border-marble-700/50 hover:bg-gray-50 dark:hover:bg-marble-700/30 transition-all duration-150 hover:shadow-md"
                  >
                    <td className="py-4 px-4">
                      <span className="font-mono text-gray-800 dark:text-primary-200 font-medium transition-colors">
                        {column.column}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getTypeColor(column.type)}`}>
                        {column.type}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right font-mono text-gray-700 dark:text-marble-300 transition-colors">
                      {(column.count || 0).toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-right font-mono text-gray-700 dark:text-marble-300 transition-colors">
                      {(column.unique_vals || 0).toLocaleString()}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className={`font-mono ${column.na_count > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                        {(column.na_count || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <div className="w-16 h-2 bg-gray-200 dark:bg-marble-700 rounded-full overflow-hidden transition-colors">
                          <div 
                            className={`h-full ${completeness >= 95 ? 'bg-green-500' : completeness >= 75 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${completeness}%` }}
                          ></div>
                        </div>
                        <span className="font-mono text-sm text-gray-700 dark:text-marble-300 w-12 transition-colors">
                          {completeness}%
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <div className="w-16 h-2 bg-gray-200 dark:bg-marble-700 rounded-full overflow-hidden transition-colors">
                          <div 
                            className={`h-full ${uniqueness >= 75 ? 'bg-blue-500' : uniqueness >= 25 ? 'bg-purple-500' : 'bg-gray-500'}`}
                            style={{ width: `${Math.min(uniqueness, 100)}%` }}
                          ></div>
                        </div>
                        <span className="font-mono text-sm text-gray-700 dark:text-marble-300 w-12 transition-colors">
                          {uniqueness}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SchemaTable;
